import type { ColumnDef, VisibilityState } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Columns3, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react'

interface ColumnPickerProps<TData> {
  columns: ColumnDef<TData, any>[]
  columnVisibility: VisibilityState
  onColumnVisibilityChange: (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)
  ) => void
  columnOrder?: string[]
  onColumnOrderChange?: (order: string[]) => void
}

// Columns that should never be toggled or reordered
const NON_TOGGLEABLE = new Set(['select', 'actions'])

export function ColumnPicker<TData>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
}: ColumnPickerProps<TData>) {
  const getColumnId = (col: ColumnDef<TData, any>): string => {
    return (col as any).id || (col as any).accessorKey || ''
  }

  const getColumnLabel = (col: ColumnDef<TData, any>): string => {
    const header = col.header
    if (typeof header === 'string') return header
    const id = getColumnId(col)
    return id.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
  }

  const toggleableColumns = columns.filter((col) => {
    const id = getColumnId(col)
    return id && !NON_TOGGLEABLE.has(id)
  })

  // Build ordered list: use columnOrder if provided, otherwise use default column order
  const orderedColumns = (() => {
    if (!columnOrder || columnOrder.length === 0) return toggleableColumns
    const colMap = new Map(toggleableColumns.map((c) => [getColumnId(c), c]))
    const ordered: ColumnDef<TData, any>[] = []
    for (const id of columnOrder) {
      const col = colMap.get(id)
      if (col) {
        ordered.push(col)
        colMap.delete(id)
      }
    }
    // Add any columns not in the order (new columns)
    for (const col of colMap.values()) {
      ordered.push(col)
    }
    return ordered
  })()

  const isVisible = (id: string) => columnVisibility[id] !== false
  const visibleCount = toggleableColumns.filter((c) => isVisible(getColumnId(c))).length

  const moveColumn = (id: string, direction: 'up' | 'down') => {
    if (!onColumnOrderChange) return
    const currentOrder = orderedColumns.map((c) => getColumnId(c))
    const index = currentOrder.indexOf(id)
    if (index < 0) return
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= currentOrder.length) return
    const newOrder = [...currentOrder]
    ;[newOrder[index]!, newOrder[newIndex]!] = [newOrder[newIndex]!, newOrder[index]!]
    onColumnOrderChange(newOrder)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" aria-label="Choose and reorder columns">
          <Columns3 className="mr-2 h-4 w-4" aria-hidden="true" />
          Columns ({visibleCount})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {orderedColumns.map((col, index) => {
            const id = getColumnId(col)
            const visible = isVisible(id)
            return (
              <div
                key={id}
                className="flex items-center gap-1 px-2 py-1 hover:bg-accent hover:text-accent-foreground rounded-sm"
              >
                <button
                  onClick={() => {
                    onColumnVisibilityChange((prev) => ({
                      ...prev,
                      [id]: !visible,
                    }))
                  }}
                  className="flex-shrink-0 p-0.5 rounded hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring"
                  aria-label={
                    visible ? `Hide ${getColumnLabel(col)}` : `Show ${getColumnLabel(col)}`
                  }
                >
                  {visible ? (
                    <Eye className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  )}
                </button>
                <span className={`flex-1 text-sm ${visible ? '' : 'text-muted-foreground'}`}>
                  {getColumnLabel(col)}
                </span>
                {onColumnOrderChange && (
                  <div className="flex gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => moveColumn(id, 'up')}
                      disabled={index === 0}
                      className="p-0.5 rounded hover:bg-muted disabled:opacity-30 focus:outline-none focus:ring-1 focus:ring-ring"
                      aria-label={`Move ${getColumnLabel(col)} up`}
                    >
                      <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => moveColumn(id, 'down')}
                      disabled={index === orderedColumns.length - 1}
                      className="p-0.5 rounded hover:bg-muted disabled:opacity-30 focus:outline-none focus:ring-1 focus:ring-ring"
                      aria-label={`Move ${getColumnLabel(col)} down`}
                    >
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <DropdownMenuSeparator />
        <div className="px-2 py-1">
          <button
            onClick={() => {
              const update: VisibilityState = {}
              toggleableColumns.forEach((col) => {
                update[getColumnId(col)] = true
              })
              onColumnVisibilityChange((prev) => ({ ...prev, ...update }))
            }}
            className="text-xs text-primary hover:underline focus:outline-none"
          >
            Show All
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
